

import { useState } from 'react';

import { useFirebase } from '@/firebase';

interface GenerateStylePreviewInput {
  novelId: string;
  customStyle: string;
}

interface GenerateStylePreviewOutput {
  imageUrl: string;
  storagePath: string;
}

interface UseGenerateStylePreviewResult {
  generatePreview: (novelId: string, customStyle: string) => Promise<GenerateStylePreviewOutput | null>;
  loading: boolean;
  error: Error | null;
  preview: GenerateStylePreviewOutput | null;
}

/**
 * Hook for generating custom style preview images
 * @returns Function to generate preview, loading state, error, and preview data
 */
export function useGenerateStylePreview(): UseGenerateStylePreviewResult {
  const { backend } = useFirebase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [preview, setPreview] = useState<GenerateStylePreviewOutput | null>(null);

  const generatePreview = async (
    novelId: string,
    customStyle: string
  ): Promise<GenerateStylePreviewOutput | null> => {
    if (!backend || !customStyle.trim()) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await backend.ai.generateStylePreview({
        novelId,
        customStyle: customStyle.trim(),
      });

      setPreview(result);
      return result;
    } catch (err) {
      console.error('Error generating style preview:', err);
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    generatePreview,
    loading,
    error,
    preview,
  };
}
